import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, ArrowLeft, CheckCircle, WarningCircle, DownloadSimple } from "@phosphor-icons/react";

const NONE = "__none__";

export default function SignalImportWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState("input");
  const [csvText, setCsvText] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [fieldMap, setFieldMap] = useState({}); // interKO field -> csv column
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const reset = () => { setStep("input"); setCsvText(""); setAnalysis(null); setFieldMap({}); setReport(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCsvText(String(r.result || ""));
    r.readAsText(f);
  };

  const analyze = async () => {
    if (!csvText.trim()) { toast.error("Collez ou importez un CSV"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/signals/analyze", { csv: csvText });
      setAnalysis(data);
      const inv = {};
      Object.entries(data.suggested_mapping).forEach(([col, field]) => { if (field) inv[field] = col; });
      setFieldMap(inv);
      setStep("mapping");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    const mapping = {};
    Object.entries(fieldMap).forEach(([field, col]) => { if (col && col !== NONE) mapping[col] = field; });
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/signals", { csv: csvText, mapping });
      setReport(data);
      setStep("report");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const downloadErrors = () => {
    const rows = [["line", "company_name", "role_title", "reason"],
      ...report.errors.map((e) => [e.line, e.company_name, e.role_title, e.reason])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "interko_import_errors.csv";
    a.click();
  };

  const downloadTemplate = () => {
    const headers = ["company_name", "company_domain", "industry", "role_title", "description",
      "skills", "technologies", "job_family", "seniority", "contract_type", "employment_type",
      "location_raw", "city", "postal_code", "region", "country", "remote_status",
      "salary_min", "salary_max", "salary_currency", "source", "source_job_id", "source_url", "posted_at"];
    const sample = ["Novaxis Cloud", "novaxis-cloud.fr", "Éditeur SaaS / Cloud", "Ingénieur DevOps Senior",
      "Renforcement de l'équipe plateforme pour industrialiser les déploiements Kubernetes multi-cloud.",
      "AWS;Kubernetes;Terraform;CI/CD", "Docker;Helm;ArgoCD", "DevOps", "Senior", "Régie", "CDI",
      "Lille, France", "Lille", "59000", "Hauts-de-France", "France", "hybride",
      "450", "550", "EUR", "LinkedIn", "LI-123456", "https://exemple.com/offre/123", "2026-06-01"];
    const rows = [headers, sample];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "interko_modele_signaux.csv";
    a.click();
  };

  const fields = analysis?.target_fields || [];
  const required = analysis?.required || [];
  const cols = analysis?.columns || [];
  const mappedFields = new Set(Object.entries(fieldMap).filter(([, c]) => c && c !== NONE).map(([f]) => f));
  const missingReq = required.filter((f) => !mappedFields.has(f));

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-surface border-border max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="signal-import-wizard">
        <DialogHeader>
          <DialogTitle className="font-display">Importer des signaux — CSV</DialogTitle>
          <DialogDescription className="text-muted">
            {step === "input" && "Chargez votre fichier. Les champs seront normalisés et enrichis automatiquement."}
            {step === "mapping" && "Vérifiez la correspondance des colonnes puis lancez l'import."}
            {step === "report" && "Rapport d'import."}
          </DialogDescription>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted">Format attendu : CSV avec en-têtes de colonnes.</span>
              <button onClick={downloadTemplate} data-testid="download-template-btn"
                className="flex items-center gap-1.5 text-xs text-accent border border-accent/30 rounded-md px-3 py-1.5 hover-lift hover:border-accent/60">
                <DownloadSimple size={14} /> Télécharger le modèle CSV
              </button>
            </div>
            <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="wizard-file"
              className="block w-full text-sm text-muted file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-surface-2 file:text-fg file:cursor-pointer" />
            <Textarea className="bg-surface-2 border-border text-fg min-h-[180px] font-mono text-xs" value={csvText}
              onChange={(e) => setCsvText(e.target.value)} placeholder="company,title,job_description,job_location,url,skills,source,country,job_id&#10;…" data-testid="wizard-textarea" />
            <div className="text-xs text-muted">Obligatoires : company_name, role_title, description, source, country. Les colonnes usuelles (company, title, url…) sont reconnues automatiquement.</div>
            <button onClick={analyze} disabled={busy} data-testid="wizard-analyze-btn"
              className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift w-full disabled:opacity-60">
              {busy ? "Analyse…" : "Analyser le fichier"}
            </button>
          </div>
        )}

        {step === "mapping" && analysis && (
          <div className="py-2 space-y-4">
            {missingReq.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="missing-required">
                <WarningCircle size={16} /> Champs obligatoires non mappés : {missingReq.join(", ")}
              </div>
            )}
            {analysis.unknown_columns?.length > 0 && (
              <div className="text-xs text-muted">Colonnes non reconnues (ignorées) : {analysis.unknown_columns.join(", ")}</div>
            )}

            <div className="border border-border rounded-md max-h-[220px] overflow-y-auto" data-testid="mapping-grid">
              {fields.map((f) => (
                <div key={f} className="flex items-center justify-between gap-3 px-3 py-1.5 border-b border-border/50">
                  <span className={`text-xs font-mono ${required.includes(f) ? "text-accent" : "text-fg/80"}`}>{f}{required.includes(f) ? " *" : ""}</span>
                  <Select value={fieldMap[f] || NONE} onValueChange={(v) => setFieldMap((m) => ({ ...m, [f]: v }))}>
                    <SelectTrigger className="w-48 h-8 bg-surface-2 border-border text-xs" data-testid={`map-${f}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>—</SelectItem>
                      {cols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div>
              <div className="overline mb-2">Aperçu ({analysis.preview.length} premières lignes)</div>
              <div className="border border-border rounded-md overflow-x-auto" data-testid="preview-table">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-muted border-b border-border">
                    <th className="px-2 py-1.5">Rôle</th><th className="px-2 py-1.5">Entreprise</th><th className="px-2 py-1.5">Ville</th><th className="px-2 py-1.5">Compétences</th><th className="px-2 py-1.5">Pays</th>
                  </tr></thead>
                  <tbody>
                    {analysis.preview.map((p, i) => (
                      <tr key={i} className="border-b border-border/40">
                        <td className="px-2 py-1.5 text-fg">{p.row.role_title || "—"}</td>
                        <td className="px-2 py-1.5 text-muted">{p.row.company_name || "—"}</td>
                        <td className="px-2 py-1.5 text-muted">{p.row.city || "—"}</td>
                        <td className="px-2 py-1.5 text-muted font-mono truncate max-w-[160px]">{(p.row.skills || []).join(", ")}</td>
                        <td className="px-2 py-1.5 text-muted">{p.row.country || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={() => setStep("input")} className="flex items-center gap-1.5 text-sm text-muted hover:text-fg px-3 py-2.5"><ArrowLeft size={15} /> Retour</button>
              <button onClick={runImport} disabled={busy || missingReq.length > 0} data-testid="wizard-import-btn"
                className="flex-1 bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-60">
                {busy ? "Import en cours…" : "Importer les signaux"}
              </button>
            </div>
          </div>
        )}

        {step === "report" && report && (
          <div className="py-2 space-y-4" data-testid="import-report">
            <div className="flex items-center gap-2 text-accent"><CheckCircle size={20} weight="fill" /> Import terminé</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                ["Lignes analysées", report.rows_analyzed], ["Signaux créés", report.created],
                ["Mis à jour", report.updated], ["Doublons", report.duplicates],
                ["Ignorées", report.skipped], ["Entreprises créées", report.companies_created],
              ].map(([k, v]) => (
                <div key={k} className="bg-surface-2 border border-border rounded-md p-3">
                  <div className="font-mono text-2xl font-bold text-fg">{v}</div>
                  <div className="text-xs text-muted">{k}</div>
                </div>
              ))}
            </div>
            {report.errors?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-destructive flex items-center gap-1.5"><WarningCircle size={15} /> {report.errors.length} erreur(s)</span>
                  <button onClick={downloadErrors} data-testid="download-errors-btn" className="text-xs text-accent flex items-center gap-1 hover:underline"><DownloadSimple size={14} /> Télécharger les erreurs (CSV)</button>
                </div>
                <div className="border border-border rounded-md max-h-[140px] overflow-y-auto text-xs">
                  {report.errors.slice(0, 30).map((e, i) => (
                    <div key={i} className="px-3 py-1.5 border-b border-border/40 text-muted"><span className="text-fg/70">L.{e.line}</span> · {e.reason}</div>
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => close(false)} className="bg-surface-2 border border-border text-fg font-semibold px-5 py-2.5 rounded-md hover-lift w-full">Fermer</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
